import { useState, useCallback, useMemo, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import { format } from 'date-fns'
import {
  PiAddressBook,
  PiAlarm,
  PiBarcode,
  PiBell,
  PiBuildings,
  PiCalendarBlank,
  PiCalendarStar,
  PiCaretDown,
  PiCheck,
  PiCopy,
  PiCurrencyDollar,
  PiDownloadSimple,
  PiEnvelope,
  PiEnvelopeOpen,
  PiFileText,
  PiHourglass,
  PiListBullets,
  PiNotepad,
  PiPackage,
  PiPaperclip,
  PiPaperPlaneRight,
  PiPencilSimple,
  PiPushPin,
  PiPushPinSlash,
  PiSparkle,
  PiTrash,
  PiTray,
  PiUploadSimple,
  PiUser,
} from 'react-icons/pi'
import { Spinner } from 'react-bootstrap'
import { FBPage } from 'components/FbUI/Page/FBPage'
import FbButton from 'components/FbUI/FbButton'
import { MetricCard } from 'components/FbUI/MetricCard'
import {
  ds2026DropdownContentClass,
  ds2026DropdownItemClass,
} from 'components/FbUI/ds2026DropdownMenu'
import { EntityDetailLayout } from 'components/EntityDetailPage/EntityDetailLayout'
import { SidebarSection } from 'components/EntityDetailPage/SidebarSection'
import { SalesStageBar } from 'components/SalesStage/SalesStageBar'
import { getStageColorHex } from 'components/SalesStage/stageColors'
import { colors } from 'styles/tokens'
import { NoteManager } from 'components/Notes/NoteManager'
import { Editor } from 'components/Editor/Editor'
import { EmailsModal } from 'components/NewCompanyPage/Modals/EmailsModal/EmailsModal'
import { ContactCompany } from 'models/companies'
import { DealDetail } from 'models/deals'
import { INote } from 'models/contact_companies'
import { SalesStage } from 'models/sales_stages'
import useApiService from 'services/api'
import { useGatedStageMap } from 'services/captureForms'
import { CaptureForm } from 'models/captureForms'
import { CaptureFormModal } from 'components/CaptureForms/CaptureFormModal'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { usePinTarget } from 'components/QuickAccess/usePinTarget'
import { useRecordVisit } from 'components/QuickAccess/useRecordVisit'
import {
  getDaysSinceColorClass,
  usdFormatterOrFallback,
} from 'utils/formatting'
import { Dropdown, IDropdownItem } from 'components/UI/Dropdown/Dropdown'
import { ResponsiveTabs, ResponsiveTabConfig } from 'components/Tabs'
import { cn } from 'components/UI/cn'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { MULTI_VALUE_SELECT_COLORS } from 'utils/constants'
import { EditDealModal } from 'components/Deals/EditDealModal/EditDealModal'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { FilterIdentifier } from 'models/saved_view'
import DealNotesFilterset from './DealNotesFilterset'
import { DataTableProvider, useDataTableContext } from 'components/DataTable'
import TagAssignModal from 'components/Modals/AssignmentModal/TagAssignModal'
import ReminderModal from 'components/Reminders/ReminderModal'
import { statusColors, reminderTypeFormat } from 'components/Reminders/utils'
import { ReminderListItem } from 'models/reminders'
import DealRemindersFilterset from './DealRemindersFilterset'
import { EditDealLocationsModal } from './EditDealLocationsModal'
import { EditRelatedCompaniesModal } from './EditRelatedCompaniesModal'
import { EditDealProductModal } from './EditDealProductModal'
import { DateRangePicker } from './DateRangePicker'
import { TreeList, type TreeNode } from 'components/UI/TreeList'
import { UploadDocumentModal } from 'components/DocumentLibrary/UploadDocumentModal'
import {
  getDocuments,
  useDeleteDocument,
  type Document,
} from 'services/documents'
import { formatBytes } from 'utils/formatBytes'
import DealDocumentsFilterset from './DealDocumentsFilterset'
import { DealEventHistory } from './DealEventHistory'
import { DealRecommendationsTab } from './DealRecommendationsTab'
import { useEventHistoryFilterStore } from 'components/NewCompanyPage/EventHistory/EventHistoryFilterStore'
import { NotFoundContent } from '../not-found-content'

const TAGS_COLLAPSED_COUNT = 4

type DealDetailTab =
  | 'activity'
  | 'emails'
  | 'notes'
  | 'reminders'
  | 'documents'
  | 'recommendations'

/** Creates a right-aligned "More" tree node for truncated lists. */
function moreTreeNode(id: string, text: string, onClick: () => void): TreeNode {
  return {
    id,
    label: (
      <div className="text-right">
        <button
          type="button"
          onClick={onClick}
          className="text-fg-secondary hover:underline underline text-xs"
        >
          {text}
        </button>
      </div>
    ),
  }
}

/** Group items by date for the pistachio timeline display. */
function groupByDate<T>(
  items: T[],
  getDate: (item: T) => string | null | undefined
): { date: string; displayDate: string; items: T[] }[] {
  const groupMap = new Map<string, T[]>()

  for (const item of items) {
    const raw = getDate(item)
    if (!raw) continue
    const dateKey = dayjs(raw).format('YYYY-MM-DD')
    if (!groupMap.has(dateKey)) groupMap.set(dateKey, [])
    groupMap.get(dateKey)!.push(item)
  }

  const today = dayjs()
  const groups: { date: string; displayDate: string; items: T[] }[] = []

  for (const [dateKey, groupItems] of groupMap) {
    const d = dayjs(dateKey)
    let relative = d.format('ddd')
    if (d.isSame(today, 'day')) relative = 'Today'
    else if (d.isSame(today.subtract(1, 'day'), 'day')) relative = 'Yesterday'
    else if (d.isSame(today.add(1, 'day'), 'day')) relative = 'Tomorrow'

    groups.push({
      date: dateKey,
      displayDate: `${relative}, ${d.format('MMM D')}`,
      items: groupItems,
    })
  }

  return groups
}

/** Pill-style status badge matching the homepage SummaryStatsHeader chips:
 *  rounded-square colored icon tile, bold count, then label. When `days`
 *  is 0, renders `zeroText` as a single bold span instead. Color classes
 *  pair a Figma badge background with its matching strong text color. */
/**
 * Freshness → DealStatusBadge color classes (bg + text + colored border),
 * keyed by the `text-*` class `getDaysSinceColorClass` returns so the
 * day thresholds stay single-sourced in formatting.ts. Mirrors DealCard's
 * FRESHNESS_PILL_CLASS, with the deal-detail badge's mid-tone border added.
 */
const FRESHNESS_BADGE_CLASS: Record<string, string> = {
  'text-success':
    'bg-success-bg-subtle text-success-strong border !border-green-400',
  'text-warning':
    'bg-warning-bg-subtle text-warning-strong border !border-amber-400',
  'text-error': 'bg-error-bg-subtle text-error-strong border !border-red-400',
}

/** Resolve the "days since last update" badge color from the day count. */
function freshnessBadgeClass(days: number): string {
  return (
    FRESHNESS_BADGE_CLASS[getDaysSinceColorClass(days, 'text')] ??
    FRESHNESS_BADGE_CLASS['text-success']
  )
}

function DealStatusBadge({
  icon,
  colorClasses,
  days,
  suffix,
  zeroText,
}: {
  icon: React.ReactNode
  colorClasses: string
  days: number
  suffix: string
  zeroText: string
}) {
  return (
    <div className="flex items-center gap-2">
      <span
        className={cn(
          'size-7 rounded-full flex items-center justify-center flex-shrink-0',
          colorClasses
        )}
      >
        {icon}
      </span>
      {days === 0 ? (
        <span className="text-base font-normal leading-none text-fg">
          {zeroText}
        </span>
      ) : (
        <>
          <span className="text-base font-bold leading-none tabular-nums text-fg">
            {days}
          </span>
          <span className="text-base font-normal leading-none text-fg-secondary">
            {days === 1 ? 'day' : 'days'} {suffix}
          </span>
        </>
      )}
    </div>
  )
}

/** Card shell matching the activity-tab `BaseEventCard` visual: circular
 *  green icon on the left, white rounded card on the right with shadow.
 *  Used by the Notes / Reminders / Documents card components. */
function DealActivityCardShell({
  icon,
  children,
}: {
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="flex items-start gap-3">
      <div
        className="flex-shrink-0 w-9 h-9 rounded-full bg-green-50 flex items-center justify-center relative z-[2]"
        style={{ border: '1px solid rgba(21, 128, 61, 0.5)' }}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0 bg-white rounded-lg px-3 py-3 shadow-sm">
        {children}
      </div>
    </div>
  )
}

/** Date-group container matching the activity-tab visual: gray date header
 *  + hairline divider, followed by a gray rounded container holding the
 *  cards. Used by the Notes / Reminders / Documents tabs. */
function TimelineDateGroup({
  displayDate,
  children,
}: {
  displayDate: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <span className="text-base text-gray-500 font-inter whitespace-nowrap tabular-nums">
          {displayDate}
        </span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>
      <div className="bg-gray-50 rounded-lg p-3">
        <div className="space-y-4">{children}</div>
      </div>
    </div>
  )
}

// ─── Deal Detail Page ─────────────────────────────────────────────────────────

/** Top-level page component for viewing and managing a single deal. */
export function DealDetailPage() {
  const { dealId } = useParams<{ dealId: string }>()
  const api = useApiService()
  const queryClient = useQueryClient()

  const dealIdNum = dealId ? parseInt(dealId) : undefined
  const { data: deal, isLoading, isError, refetch } = api.useGetDeal(dealIdNum)
  const { data: stages } = api.useGetSalesStages()
  const { isSettingEnabled } = usePreferences()
  const customOrderEnabled = isSettingEnabled('sales_stage_custom_order')

  const [activeTab, setActiveTab] = useState<DealDetailTab>('activity')
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [showEditDeal, setShowEditDeal] = useState(false)
  const [showEditLocations, setShowEditLocations] = useState(false)
  const [showEditProduct, setShowEditProduct] = useState(false)
  const [showReminderModal, setShowReminderModal] = useState(false)
  const [showUploadDoc, setShowUploadDoc] = useState(false)
  const [showEditRelatedCompanies, setShowEditRelatedCompanies] =
    useState(false)
  const { enableQuickAccess } = useFeatureFlagService()
  const {
    isPinned: pinnedToHome,
    toggle: togglePin,
    isPending: pinPending,
  } = usePinTarget({ targetType: 'deal', targetId: dealIdNum ?? 0 })
  useRecordVisit(dealIdNum ? { targetType: 'deal', targetId: dealIdNum } : null)
  const [docsRefreshKey, setDocsRefreshKey] = useState(0)
  const [showTagModal, setShowTagModal] = useState(false)
  const [tagsExpanded, setTagsExpanded] = useState(false)
  const [hoverCompanies, setHoverCompanies] = useState(false)
  const [hoverRelatedCompanies, setHoverRelatedCompanies] = useState(false)
  const [hoverProducts, setHoverProducts] = useState(false)
  const [hoverTags, setHoverTags] = useState(false)
  const [copiedId, setCopiedId] = useState(false)
  const navigate = useNavigate()

  // Reset the shared EventHistory filter store when the user navigates to
  // a different deal — but NOT on within-page tab switches. Previously
  // this lived inside DealEventHistory's mount effect, which fired every
  // time the Activity / Emails tab swap remounted the component and
  // silently destroyed the user's active filters (bot finding on #3811).
  useEffect(() => {
    useEventHistoryFilterStore.getState().clearAllFilters()
  }, [dealIdNum])

  const handleDeleteDeal = useCallback(async () => {
    if (!dealIdNum) return
    if (
      !window.confirm(
        'Are you sure you want to delete this deal? This cannot be undone.'
      )
    )
      return
    try {
      await api.deleteDeal(dealIdNum)
      toast.success('Deal deleted')
      navigate('/deals')
    } catch {
      toast.error('Failed to delete deal')
    }
  }, [dealIdNum, api, navigate])

  const invalidateActivity = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['dealActivity', dealIdNum] })
  }, [queryClient, dealIdNum])

  /** Refetch deal data + refresh activity feed after any deal mutation. */
  const handleDealSaved = useCallback(async () => {
    await refetch()
    invalidateActivity()
  }, [refetch, invalidateActivity])

  const handleSaveRelatedCompanies = useCallback(
    async (added: number[], removed: number[]) => {
      if (!dealIdNum) return
      try {
        if (removed.length > 0) {
          await api.manageDealRelatedCompanies(dealIdNum, removed, 'remove')
        }
        if (added.length > 0) {
          await api.manageDealRelatedCompanies(dealIdNum, added, 'add')
        }
        toast.success('Related companies updated')
        await handleDealSaved()
      } catch {
        toast.error('Failed to update related companies')
      }
    },
    [dealIdNum, api, handleDealSaved]
  )

  // Stage moves into a gated stage (e.g. Closed Won / Closed Lost) open the
  // required capture instead of moving directly; the capture commits the stage
  // move on submit. Mirrors SalesStageSelectModal and the Kanban board.
  const { gatedStageMap, isLoading: gatingLoading } = useGatedStageMap()
  const [pendingStageCapture, setPendingStageCapture] = useState<{
    stageId: number
    form: CaptureForm
  } | null>(null)

  const handleStageChange = useCallback(
    // `null` clears the deal back to "no stage".
    async (stageId: number | null) => {
      if (!dealIdNum) return

      // Gating not resolved yet: refuse the move rather than let it slip through
      // ungated during the capture-forms fetch window. The server enforces the
      // gate too, but this avoids a confusing optimistic move that then 400s.
      if (gatingLoading) return

      // Gated stage: open the capture, which moves the stage on submit. The
      // direct edit below is skipped so the stage never changes uncaptured.
      if (stageId !== null && gatedStageMap.has(stageId)) {
        setPendingStageCapture({
          stageId,
          form: gatedStageMap.get(stageId)!,
        })
        return
      }

      // Optimistic update: immediately reflect the new stage in the UI
      const queryKey = ['deal', String(dealIdNum)]
      const previous = queryClient.getQueryData<DealDetail>(queryKey)
      if (previous) {
        queryClient.setQueryData<DealDetail>(queryKey, {
          ...previous,
          sales_stage: stageId,
        })
      }

      try {
        await api.editDeal(dealIdNum, { sales_stage: stageId })
        toast.success(stageId === null ? 'Stage cleared' : 'Stage updated')
        // Do NOT immediately refetch the deal here. Reads are routed to a
        // lagging read-replica (see server/__project__/db_routers.py — writes
        // hit the primary, reads hit read-replica-1), so refetching the instant
        // the PATCH resolves races replication and pulls the *pre-write* stage
        // back over our optimistic update — the stage visibly snaps back after
        // the success toast (most obvious when clearing to "no stage"). The
        // optimistic setQueryData above is authoritative; mark the deal query
        // stale so server-computed fields (time_in_stage, etc.) refresh on the
        // next mount/focus, by which point the replica has caught up. Mirrors
        // the Kanban board's CDC-safe reconcile (DealsKanbanBoard
        // handleEditModalClose).
        queryClient.invalidateQueries({ queryKey, refetchType: 'none' })
        invalidateActivity()
      } catch {
        // Rollback on failure
        if (previous) {
          queryClient.setQueryData<DealDetail>(queryKey, previous)
        }
        toast.error('Failed to update stage')
      }
    },
    [
      dealIdNum,
      api,
      queryClient,
      invalidateActivity,
      gatedStageMap,
      gatingLoading,
    ]
  )

  // Reflect the stage move once the gated capture commits it, using the same
  // CDC-safe optimistic update as the direct path (no immediate refetch).
  const handleGatedCaptureSuccess = useCallback(() => {
    if (!dealIdNum || !pendingStageCapture) return
    const queryKey = ['deal', String(dealIdNum)]
    const previous = queryClient.getQueryData<DealDetail>(queryKey)
    if (previous) {
      queryClient.setQueryData<DealDetail>(queryKey, {
        ...previous,
        sales_stage: pendingStageCapture.stageId,
      })
    }
    queryClient.invalidateQueries({ queryKey, refetchType: 'none' })
    invalidateActivity()
    setPendingStageCapture(null)
  }, [dealIdNum, pendingStageCapture, queryClient, invalidateActivity])

  const handleCopyId = useCallback(() => {
    if (!deal?.identifier) return
    navigator.clipboard.writeText(deal.identifier).then(() => {
      setCopiedId(true)
      toast.success('Deal ID copied')
      setTimeout(() => setCopiedId(false), 2000)
    })
  }, [deal?.identifier])

  const entityBreadcrumbs = useMemo(
    () => (deal?.identifier ? [{ label: `#${deal.identifier}` }] : []),
    [deal?.identifier]
  )

  if (isLoading) {
    return (
      <FBPage className="gap-y-4" noContainer innerPageClassName="!pb-0 !pr-0">
        <div className="flex items-center justify-center h-64">
          <Spinner animation="border" />
        </div>
      </FBPage>
    )
  }

  if (!deal || isError) {
    return <NotFoundContent />
  }

  const allStages = customOrderEnabled
    ? [...(stages ?? [])].sort((a, b) => a.order - b.order)
    : (stages ?? [])

  const dealName =
    deal.name ||
    deal.contact_company?.name ||
    `Deal #${deal.identifier ?? deal.id}`

  // Days since last CRM update
  const daysSinceModified = deal.modified
    ? Math.floor(dayjs().diff(dayjs(deal.modified), 'day'))
    : null

  // For EmailsModal: build a minimal ContactCompany shape
  const contactCompanyForModal = deal.contact_company
    ? ({
        id: deal.contact_company.id,
        name: deal.contact_company.name,
      } as unknown as ContactCompany)
    : undefined

  const displayTags = deal.deal_taglist ?? []
  const visibleTags = tagsExpanded
    ? displayTags
    : displayTags.slice(0, TAGS_COLLAPSED_COUNT)

  return (
    <EntityDetailLayout
      entityBreadcrumbs={entityBreadcrumbs}
      scrollMode="content-fixed"
      gap={0}
      contentGap={0}
      innerPageClassName="!p-0"
      // Horizontal inset matching the company page's content column, where each
      // accordion sits inside a `px-4` wrapper (CompanyContentSections). The deal
      // column has gap-0 to the sidebar, so without this the header + activity
      // card would butt flush against the seam; px-4 gives the same 16px
      // "breathing room" the company page editor column has.
      contentClassName="px-4"
      // Whole page sits on tailwind gray-50; the white floating sidebar card
      // and the content both share that backdrop so there's no seam between
      // the two columns.
      contentWrapperClassName="bg-gray-50"
      // Insets live on the column (padding), NOT as margins on the card.
      // The card is h-full; margins would push it past 100% of the column
      // and make the whole column scroll instead of the card's inner area.
      // Top inset is intentionally smaller than the 20px sides — the card's
      // own p-5 already adds visual breathing room at the top. The content
      // column's top inset lives on the sticky header itself (pt-3) so the
      // 12px gap above the header persists even when it's pinned on scroll.
      sidebarWrapperClassName="bg-gray-50 pl-5 pt-3 pr-5"
      sidebar={
        // Floating panel: white card with border + shadow on the gray
        // column, mirroring the company page sidebar. h-full + min-h-0 +
        // inner overflow-y-auto keep the card pinned to the viewport and
        // scroll only its content.
        <div className="flex h-full min-h-0 w-[340px] flex-shrink-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto overflow-x-hidden p-3">
            {/* Stat cards — DS2026 MetricCard, matching the homepage
                Performance dashboard tiles. Stacked vertically so the long
                deal value fits the narrow panel. */}
            <div className="flex flex-col gap-3 mb-1">
              <MetricCard
                className="border-0 bg-gray-50"
                icon={<PiCurrencyDollar size={18} />}
                label={
                  deal.is_not_recurring
                    ? 'Total Deal Value'
                    : deal.monthly_revenue_override != null
                      ? '1yr Revenue Override'
                      : '1yr Revenue Forecast'
                }
                value={usdFormatterOrFallback(deal.revenue_display)}
              />
              <MetricCard
                className="border-0 bg-gray-50"
                icon={<PiCalendarBlank size={18} />}
                label="Est. Close Date"
                value={
                  deal.close_date_override
                    ? dayjs(deal.close_date_override).format('MMM D, YYYY')
                    : '-'
                }
              />
            </div>

            {/* Summary */}
            <SidebarSection title="Summary" defaultOpen>
              <DealInfoRows deal={deal} stages={allStages} />
            </SidebarSection>

            {/* Primary Company */}
            <div
              onMouseEnter={() => setHoverCompanies(true)}
              onMouseLeave={() => setHoverCompanies(false)}
            >
              <SidebarSection
                title="Primary Company"
                defaultOpen
                headerRight={
                  <button
                    type="button"
                    className={cn(
                      'transition-opacity p-1 rounded hover:bg-surface-hover',
                      hoverCompanies ? 'opacity-100' : 'opacity-0'
                    )}
                    onClick={() => setShowEditLocations(true)}
                    title="Edit locations"
                  >
                    <PiPencilSimple size={14} className="text-fg-secondary" />
                  </button>
                }
              >
                <PrimaryCompanySection deal={deal} />
              </SidebarSection>
            </div>

            {/* Related Companies */}
            <div
              onMouseEnter={() => setHoverRelatedCompanies(true)}
              onMouseLeave={() => setHoverRelatedCompanies(false)}
            >
              <SidebarSection
                title="Related Companies"
                defaultOpen={(deal.related_companies ?? []).length > 0}
                headerRight={
                  <button
                    type="button"
                    className={cn(
                      'transition-opacity p-1 rounded hover:bg-surface-hover',
                      hoverRelatedCompanies ? 'opacity-100' : 'opacity-0'
                    )}
                    onClick={() => setShowEditRelatedCompanies(true)}
                    title="Edit related companies"
                  >
                    <PiPencilSimple size={14} className="text-fg-secondary" />
                  </button>
                }
              >
                <RelatedCompaniesSection deal={deal} />
              </SidebarSection>
            </div>

            {/* Products */}
            {deal.product_detail && (
              <div
                onMouseEnter={() => setHoverProducts(true)}
                onMouseLeave={() => setHoverProducts(false)}
              >
                <SidebarSection
                  title="Products"
                  defaultOpen
                  headerRight={
                    <button
                      type="button"
                      className={cn(
                        'transition-opacity p-1 rounded hover:bg-surface-hover',
                        hoverProducts ? 'opacity-100' : 'opacity-0'
                      )}
                      onClick={() => setShowEditProduct(true)}
                    >
                      <PiPencilSimple size={14} className="text-fg-secondary" />
                    </button>
                  }
                >
                  <DealProductsSection
                    product={deal.product_detail}
                    selectedVariantId={deal.product_variant_id}
                  />
                </SidebarSection>
              </div>
            )}

            {/* Tags */}
            <div
              className="group/tags"
              onMouseEnter={() => setHoverTags(true)}
              onMouseLeave={() => setHoverTags(false)}
            >
              <SidebarSection
                title="Tags"
                defaultOpen={displayTags.length > 0}
                headerRight={
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      className={cn(
                        'transition-opacity p-1 rounded hover:bg-surface-hover',
                        hoverTags ? 'opacity-100' : 'opacity-0'
                      )}
                      onClick={() => setShowTagModal(true)}
                    >
                      <PiPencilSimple size={14} className="text-fg-secondary" />
                    </button>
                    {displayTags.length > TAGS_COLLAPSED_COUNT && (
                      <button
                        type="button"
                        className="text-xs text-brand hover:underline"
                        onClick={() => setTagsExpanded((e) => !e)}
                      >
                        {tagsExpanded ? 'Hide' : 'View All'}
                      </button>
                    )}
                  </div>
                }
              >
                <DealTagsSection tags={visibleTags} />
              </SidebarSection>
            </div>

            {/* Distributors */}
            <SidebarSection
              title="Distributors"
              defaultOpen={
                (deal.contact_company?.distributors ?? []).length > 0
              }
            >
              <NameListSection
                items={deal.contact_company?.distributors ?? []}
                emptyText="No distributors."
              />
            </SidebarSection>

            {/* GPOs */}
            <SidebarSection
              title="GPOs"
              defaultOpen={(deal.contact_company?.gpos ?? []).length > 0}
            >
              <NameListSection
                items={deal.contact_company?.gpos ?? []}
                emptyText="No GPOs."
              />
            </SidebarSection>

            {/* Contacts */}
            <SidebarSection
              title="Contacts"
              defaultOpen={(deal.contacts ?? []).length > 0}
            >
              <DealContactsSection contacts={deal.contacts ?? []} />
            </SidebarSection>
          </div>
        </div>
      }
    >
      {/* Pinned header: deal info, stage bar, composer. The tabs row and
          activity feed live in the flex-1 card below and scroll separately. */}
      <div className="bg-gray-50 flex flex-col gap-3 pt-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-col gap-0.5">
            {deal.identifier && (
              <button
                type="button"
                onClick={handleCopyId}
                className="text-base text-fg-secondary hover:text-fg flex items-center gap-1 w-fit transition-colors"
              >
                #{deal.identifier}
                {copiedId ? <PiCheck size={12} /> : <PiCopy size={12} />}
              </button>
            )}
            <h1 className="text-3xl font-semibold text-fg leading-tight">
              {dealName}
            </h1>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-2.5">
              {daysSinceModified !== null && (
                <DealStatusBadge
                  icon={<PiHourglass size={14} />}
                  colorClasses={freshnessBadgeClass(daysSinceModified)}
                  days={daysSinceModified}
                  suffix="since last update"
                  zeroText="Updated today"
                />
              )}
              {deal.days_since_sent != null && (
                <DealStatusBadge
                  icon={<PiPaperPlaneRight size={14} />}
                  colorClasses="bg-error-bg-subtle text-error-strong border !border-red-400"
                  days={deal.days_since_sent}
                  suffix="since sent"
                  zeroText="Sent today"
                />
              )}
              {deal.days_since_received != null && (
                <DealStatusBadge
                  icon={<PiTray size={14} />}
                  colorClasses="bg-warning-bg-subtle text-warning-strong border !border-amber-400"
                  days={deal.days_since_received}
                  suffix="since received"
                  zeroText="Received today"
                />
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {contactCompanyForModal && (
              <button
                type="button"
                onClick={() => setShowEmailModal(true)}
                className="inline-flex items-center gap-2 h-10 rounded-lg bg-ds2026-btn-outline-bg border border-ds2026-btn-outline-stroke px-4 text-base font-medium text-ds2026-btn-outline-text tracking-[-0.2px] whitespace-nowrap transition-colors hover:bg-ds2026-btn-outline-hover-bg hover:text-ds2026-btn-outline-hover-text focus:outline-none focus:ring-0"
              >
                <PiEnvelope size={16} />
                Inbox
                {deal.assigned_thread_count != null &&
                  deal.assigned_thread_count > 0 && (
                    <span className="rounded-full bg-neutral-100 px-1.5 py-0.5 text-xs font-medium tabular-nums text-neutral-700">
                      {deal.assigned_thread_count}
                    </span>
                  )}
              </button>
            )}
            <Dropdown
              align="end"
              contentClassName={ds2026DropdownContentClass}
              itemClassName={ds2026DropdownItemClass}
              items={
                [
                  {
                    label: 'Edit this deal',
                    icon: <PiPencilSimple size={16} />,
                    callback: () => setShowEditDeal(true),
                  },
                  {
                    label: 'Edit related companies',
                    icon: <PiBuildings size={16} />,
                    callback: () => setShowEditRelatedCompanies(true),
                  },
                  {
                    label: pinnedToHome
                      ? 'Unpin from homepage'
                      : 'Pin to homepage',
                    icon: pinnedToHome ? (
                      <PiPushPinSlash size={16} />
                    ) : (
                      <PiPushPin size={16} />
                    ),
                    hidden: !enableQuickAccess,
                    disabled: pinPending,
                    callback: () => {
                      togglePin()
                      toast.success(
                        pinnedToHome
                          ? 'Unpinned from homepage'
                          : 'Pinned to homepage'
                      )
                    },
                  },
                  {
                    label: 'Schedule a reminder',
                    icon: <PiCalendarStar size={16} />,
                    callback: () => setShowReminderModal(true),
                  },
                  {
                    label: 'Upload a document',
                    icon: <PiUploadSimple size={16} />,
                    callback: () => setShowUploadDoc(true),
                  },
                  {
                    label: 'Delete this deal',
                    icon: <PiTrash size={16} />,
                    callback: handleDeleteDeal,
                    destructive: true,
                    separatorBefore: true,
                  },
                ] satisfies IDropdownItem[]
              }
              buttonComponent={
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 px-3 h-10 rounded-lg bg-brand-bright hover:bg-brand-active text-white text-base font-medium transition-colors focus:outline-none focus:ring-0"
                >
                  <span>Actions</span>
                  <PiCaretDown size={14} />
                </button>
              }
            />
          </div>
        </div>

        {/* Sales stage bar (back/forward clusters + terminal outcome group) */}
        {allStages.length > 0 && (
          <SalesStageBar
            stages={allStages}
            activeStageId={deal.sales_stage}
            onStageChange={handleStageChange}
            onClearStage={() => handleStageChange(null)}
          />
        )}

        {/* Note composer */}
        <div className="bg-gray-50">
          <DealNoteComposer dealId={deal.id} onNoteSaved={handleDealSaved} />
        </div>

        {/* Divider separating the note composer from the activity block.
            Mirrors the company page (CompanyContentSections). py-1 adds 4px
            above and below the line on top of the surrounding gap. */}
        <div className="py-1">
          <div className="border-t border-line-subtle" />
        </div>
      </div>

      {/* Activity card: tabs + tab content. Non-collapsible and headerless —
          just a bounding container matching the company page content cards.
          flex-1 + min-h-0 makes this card claim the leftover column height; the
          header block above stays pinned and only the tab content scrolls. */}
      <div className="mt-3 flex min-h-0 flex-1 flex-col rounded-lg border border-gray-200 bg-surface shadow-md">
        {/* Tabs row — a fixed header for the activity card. It sits outside the
            scrolling tab content below, so it stays put while the activity
            feed scrolls. */}
        <div className="rounded-t-lg bg-surface px-4 pt-4 pb-2">
          <ResponsiveTabs
            tabs={DEAL_DETAIL_TABS.map((t) => {
              // Wire live counts into the static tab config so Notes /
              // Reminders / Documents show the same badge counts the
              // legacy DealTabBar rendered.
              if (t.key === 'notes' && deal.notes_count)
                return { ...t, badge: deal.notes_count }
              if (t.key === 'reminders' && deal.reminders_count)
                return { ...t, badge: deal.reminders_count }
              if (t.key === 'documents' && deal.documents_count)
                return { ...t, badge: deal.documents_count }
              return t
            })}
            selectedTab={activeTab}
            setSelectedTab={setActiveTab}
            variant="pills"
          />
        </div>
        {/* The sole scroll region. Per-tab filter rows stick to the top of
            this container (stickyTopOffset=0) since the tabs sit above it. */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
          {activeTab === 'activity' && <DealEventHistory dealId={deal.id} />}
          {activeTab === 'emails' && (
            <DealEventHistory dealId={deal.id} selectedTab="emails" />
          )}
          {activeTab === 'notes' && (
            <DealNotesTab dealId={deal.id} onNotesChanged={handleDealSaved} />
          )}
          {activeTab === 'reminders' && (
            <DealRemindersTab
              dealId={deal.id}
              contactCompanyId={deal.contact_company?.id}
              onRemindersChanged={handleDealSaved}
            />
          )}
          {activeTab === 'documents' && (
            <DealDocumentsTab
              dealId={deal.id}
              onDocumentsChanged={handleDealSaved}
              refreshKey={docsRefreshKey}
            />
          )}
          {activeTab === 'recommendations' && (
            <DealRecommendationsTab dealId={deal.id} />
          )}
        </div>
      </div>

      {/* Email Modal — pre-filtered to this deal's threads */}
      {showEmailModal && contactCompanyForModal && (
        <EmailsModal
          show={showEmailModal}
          onHide={() => setShowEmailModal(false)}
          contactCompany={contactCompanyForModal}
          lockedFilters={{
            deal_ids: [deal.id],
            dealLabel: `#${deal.identifier} - ${deal.name || 'Untitled'}`,
          }}
        />
      )}

      {/* Edit Locations Modal */}
      {deal.contact_company && (
        <EditDealLocationsModal
          show={showEditLocations}
          onClose={() => setShowEditLocations(false)}
          dealId={deal.id}
          contactCompanyId={deal.contact_company.id}
          companyTotalDoors={deal.contact_company.total_doors}
          currentApplyTo={deal.apply_to}
          onSaved={handleDealSaved}
        />
      )}

      {/* Edit Related Companies Modal */}
      {deal.contact_company && showEditRelatedCompanies && (
        <EditRelatedCompaniesModal
          show={showEditRelatedCompanies}
          onClose={() => setShowEditRelatedCompanies(false)}
          dealId={deal.id}
          primaryCompanyId={deal.contact_company.id}
          currentRelatedCompanies={deal.related_companies ?? []}
          onSave={handleSaveRelatedCompanies}
        />
      )}

      {/* Full Edit Deal Modal (same as company page) */}
      <EditDealModal
        show={showEditDeal}
        onClose={() => {
          setShowEditDeal(false)
          invalidateActivity()
        }}
        dealId={deal.id}
        dealsTableQueryKey={['deal', String(deal.id)]}
      />

      {/* Edit Product Modal */}
      <EditDealProductModal
        show={showEditProduct}
        onClose={() => setShowEditProduct(false)}
        dealId={deal.id}
        currentProductId={deal.product_id}
        currentProductVariantId={deal.product_variant_id}
        onSaved={handleDealSaved}
      />

      {/* Reminder Modal (from actions dropdown) */}
      {showReminderModal && (
        <ReminderModal
          open={showReminderModal}
          onClose={() => setShowReminderModal(false)}
          defaultDealIds={[deal.id]}
          defaultCompanyId={deal.contact_company?.id}
          onCreated={() => {
            handleDealSaved()
            setActiveTab('reminders')
          }}
        />
      )}

      {/* Required capture when moving into a gated stage (Closed Won/Lost). */}
      {pendingStageCapture && dealIdNum && (
        <CaptureFormModal
          form={pendingStageCapture.form}
          dealId={dealIdNum}
          targetStageId={pendingStageCapture.stageId}
          isOpen
          onClose={() => setPendingStageCapture(null)}
          onSuccess={handleGatedCaptureSuccess}
        />
      )}

      {/* Upload Document Modal (from actions dropdown) */}
      <UploadDocumentModal
        isOpen={showUploadDoc}
        onClose={() => setShowUploadDoc(false)}
        preSelectedDealId={deal.id}
        preSelectedDealName={deal.name}
        preSelectedCompanyId={deal.contact_company?.id}
        onSuccess={() => {
          handleDealSaved()
          setDocsRefreshKey((k) => k + 1)
          setActiveTab('documents')
        }}
      />

      {/* Tag Assign Modal */}
      {showTagModal && (
        <DataTableProvider tableKey="deal-detail-tags">
          <DealTagModalBridge
            deal={deal}
            show={showTagModal}
            handleClose={() => setShowTagModal(false)}
            onSaved={handleDealSaved}
          />
        </DataTableProvider>
      )}
    </EntityDetailLayout>
  )
}

// ─── Deal Info Rows ───────────────────────────────────────────────────────────

/** Summary info rows displayed in the sidebar (stage, owner, volume, dates). */
function DealInfoRows({
  deal,
  stages,
}: {
  deal: DealDetail
  stages: SalesStage[]
}) {
  const { preferences, isSettingEnabled } = usePreferences()
  const reportingUnit = preferences?.reporting_unit || 'lbs'
  const customOrderEnabled = isSettingEnabled('sales_stage_custom_order')

  const stageEntry = stages.find((s) => s.id === deal.sales_stage)
  const stageColor = stageEntry
    ? getStageColorHex(stageEntry, stages, customOrderEnabled)
    : null

  const volumeDisplay =
    deal.volume_display != null
      ? `${deal.volume_display.toLocaleString()} ${reportingUnit.toUpperCase()}`
      : '-'

  return (
    <div className="flex flex-col gap-2 text-sm">
      <InfoRow
        label="Name"
        value={deal.name || deal.contact_company?.name || '-'}
      />
      <InfoRow
        label="ID"
        value={deal.identifier ? `#${deal.identifier}` : '-'}
        numeric
      />
      {stageEntry && stageColor && (
        <div className="flex items-start justify-between gap-2">
          <span className="text-fg-secondary flex-shrink-0">Stage</span>
          <span
            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
            style={{ backgroundColor: stageColor.bg, color: stageColor.text }}
          >
            {stageEntry.stage}
          </span>
        </div>
      )}
      <div className="flex items-start justify-between gap-2">
        <span className="text-fg-secondary flex-shrink-0">Account Owner</span>
        <span className="text-fg text-right">
          {deal.account_owner_name || '-'}
        </span>
      </div>
      <InfoRow
        label={
          deal.is_not_recurring
            ? deal.monthly_volume_override != null
              ? 'Est. Volume Override'
              : 'Est. Volume'
            : deal.monthly_volume_override != null
              ? '1yr Volume Override'
              : '1yr Volume Forecast'
        }
        value={volumeDisplay}
        numeric
      />
      {deal.is_not_recurring && deal.monthly_revenue_override != null && (
        <InfoRow
          label="Est. Price Override"
          value={usdFormatterOrFallback(deal.monthly_revenue_override)}
          numeric
        />
      )}
      <InfoRow
        label="Date Added"
        value={deal.created ? dayjs(deal.created).format('MMM D, YYYY') : '-'}
        numeric
      />
    </div>
  )
}

function InfoRow({
  label,
  value,
  numeric = false,
}: {
  label: string
  value: string
  /** Render the value with tabular-nums for number-only strings. */
  numeric?: boolean
}) {
  return (
    <div className="flex items-start justify-between gap-2">
      <span className="text-fg-secondary flex-shrink-0">{label}</span>
      <span className={cn('text-fg text-right', numeric && 'tabular-nums')}>
        {value}
      </span>
    </div>
  )
}

// ─── Products Section ─────────────────────────────────────────────────────────

/** Sidebar tree showing the deal's product and its selected variant (or variant count summary). */
function DealProductsSection({
  product,
  selectedVariantId,
}: {
  product: NonNullable<DealDetail['product_detail']>
  selectedVariantId: number | null
}) {
  const selectedVariant = selectedVariantId
    ? product.variants.find((v) => v.id === selectedVariantId)
    : null

  const variantChildren: TreeNode[] = selectedVariant
    ? [
        {
          id: `variant-${selectedVariant.id}`,
          label: (
            <span className="font-medium text-fg">{selectedVariant.name}</span>
          ),
          children: selectedVariant.sku
            ? [
                {
                  id: `variant-${selectedVariant.id}-sku`,
                  label: (
                    <span className="text-sm text-fg-secondary tabular-nums">
                      {selectedVariant.sku}
                    </span>
                  ),
                  icon: PiBarcode,
                },
              ]
            : undefined,
        },
      ]
    : product.variants.length > 0
      ? [
          {
            id: `product-${product.id}-variant-count`,
            label: (
              <span className="text-sm text-fg-secondary">
                {product.variants.length}{' '}
                {product.variants.length === 1 ? 'variant' : 'variants'}
              </span>
            ),
          },
        ]
      : [
          {
            id: `product-${product.id}-no-variants`,
            label: (
              <span className="text-sm text-fg-secondary">No variants</span>
            ),
          },
        ]

  const nodes: TreeNode[] = [
    {
      id: `product-${product.id}`,
      label: (
        <Link to="/products" className="text-fg hover:underline font-medium">
          {product.name}
        </Link>
      ),
      icon: PiPackage,
      subtitle: product.product_format,
      defaultExpanded: true,
      children: variantChildren,
    },
  ]

  return <TreeList nodes={nodes} />
}

// ─── Tag Modal Bridge ────────────────────────────────────────────────────────

/** Wraps TagAssignModal inside a DataTableProvider, pre-selecting the single deal row. */
function DealTagModalBridge({
  deal,
  show,
  handleClose,
  onSaved,
}: {
  deal: DealDetail
  show: boolean
  handleClose: () => void
  onSaved: () => void
}) {
  const api = useApiService()
  const {
    methods: { setRowSelection, setTotalRowsInBackend, setTotalRowsInPage },
  } = useDataTableContext()

  useEffect(() => {
    setRowSelection({ 0: true })
    setTotalRowsInBackend(1)
    setTotalRowsInPage(1)
  }, [setRowSelection, setTotalRowsInBackend, setTotalRowsInPage])

  return (
    <TagAssignModal
      show={show}
      handleClose={() => {
        handleClose()
        onSaved()
      }}
      data={[deal]}
      tableQueryKey={['deal', String(deal.id)]}
      tableObjAccessor="deal_taglist"
      tagParent="deals"
      filterAndSortParams={{}}
      apiTagsAction={(values) => api.commonManageDealTags({ values })}
      idAccessor="id"
    />
  )
}

// ─── Tags Section ─────────────────────────────────────────────────────────────

const defaultTagColor = MULTI_VALUE_SELECT_COLORS[0]

function DealTagsSection({
  tags,
}: {
  tags: Array<{ id: number; tag: string; color: string | null }>
}) {
  if (tags.length === 0) {
    return <p className="text-sm text-fg-secondary">No tags assigned.</p>
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {tags.map((tag) => {
        const colorEntry = tag.color
          ? MULTI_VALUE_SELECT_COLORS.find((c) => c.background === tag.color)
          : defaultTagColor
        return (
          <span
            key={tag.id}
            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
            style={{
              backgroundColor:
                colorEntry?.background ?? defaultTagColor.background,
              color: colorEntry?.text ?? defaultTagColor.text,
              border: `1px solid ${colors['surface']}`,
            }}
          >
            {tag.tag}
          </span>
        )
      })}
    </div>
  )
}

// ─── Contacts Section ─────────────────────────────────────────────────────────

// ─── Name List Section (Distributors / GPOs) ────────────────────────────────

/** Sidebar list showing named items (distributors, GPOs) as simple text entries. */
function NameListSection({
  items,
  emptyText,
}: {
  items: Array<{ id: number; name: string }>
  emptyText: string
}) {
  if (items.length === 0) {
    return <p className="text-sm text-fg-secondary py-1">{emptyText}</p>
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((item) => (
        <span
          key={item.id}
          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-surface-hover text-fg-secondary border border-line"
        >
          {item.name}
        </span>
      ))}
    </div>
  )
}

/** Sidebar tree showing deal contacts with expandable title/email details. */
function DealContactsSection({
  contacts,
}: {
  contacts: Array<{
    id: number
    first_name: string
    last_name: string
    title: string
    email: string | null
    company_name: string | null
    company_url: string | null
  }>
}) {
  const [showAll, setShowAll] = useState(false)
  const displayed = showAll ? contacts : contacts.slice(0, 5)
  const remaining = contacts.length - 5

  if (contacts.length === 0) {
    return <p className="text-sm text-fg-secondary">No contacts assigned.</p>
  }

  const nodes: TreeNode[] = [
    ...displayed.map((contact) => {
      const companyLink =
        contact.company_name && contact.company_url ? (
          <Link
            to={contact.company_url}
            className="text-fg-secondary hover:underline"
          >
            {contact.company_name}
          </Link>
        ) : contact.company_name ? (
          <span>{contact.company_name}</span>
        ) : null

      const subtitleNode = contact.title ? (
        <span className="text-sm text-fg-secondary">
          {contact.title}
          {companyLink && <> at {companyLink}</>}
        </span>
      ) : companyLink ? (
        <span className="text-sm text-fg-secondary">{companyLink}</span>
      ) : null

      const children: TreeNode[] = [
        ...(subtitleNode
          ? [
              {
                id: `contact-${contact.id}-title`,
                label: subtitleNode,
              },
            ]
          : []),
        ...(contact.email
          ? [
              {
                id: `contact-${contact.id}-email`,
                label: (
                  <a
                    href={`mailto:${contact.email}`}
                    className="text-sm text-fg-secondary hover:underline"
                  >
                    {contact.email}
                  </a>
                ),
                icon: PiEnvelope,
              },
            ]
          : []),
      ]

      return {
        id: `contact-${contact.id}`,
        label: (
          <span className="font-medium text-fg">
            {contact.first_name} {contact.last_name}
          </span>
        ),
        icon: PiAddressBook,
        children: children.length > 0 ? children : undefined,
      }
    }),
    ...(!showAll && remaining > 0
      ? [
          moreTreeNode('more-contacts', `More (+${remaining})`, () =>
            setShowAll(true)
          ),
        ]
      : []),
  ]

  return <TreeList nodes={nodes} />
}

// ─── Primary Company Section ─────────────────────────────────────────────────

/** Sidebar tree showing the deal's primary company name and location count. */
function PrimaryCompanySection({ deal }: { deal: DealDetail }) {
  const { contact_company } = deal
  const doorsCount = deal.doors_count ?? 0
  const totalCompanyDoors = contact_company?.total_doors ?? 0

  if (!contact_company) return null

  const companyLabel = contact_company.url ? (
    <Link to={contact_company.url} className="text-brand-bright font-medium">
      {contact_company.name}
    </Link>
  ) : (
    <span className="text-fg font-medium">{contact_company.name}</span>
  )

  const coversAllLocations =
    deal.apply_to === 'entire_company' || doorsCount >= totalCompanyDoors

  const totalDoorsText = `${totalCompanyDoors.toLocaleString()} locations`
  const totalDoorsLabel = <>{totalDoorsText}</>

  const sizeNode: TreeNode[] =
    totalCompanyDoors > 0
      ? [
          {
            id: `company-${contact_company.id}-size`,
            label: (
              <span className="text-sm text-fg-secondary">
                Size:{' '}
                {coversAllLocations ? (
                  <>All {totalDoorsLabel}</>
                ) : (
                  <>
                    {doorsCount.toLocaleString()} of {totalDoorsLabel}
                  </>
                )}
              </span>
            ),
          },
        ]
      : []

  const nodes: TreeNode[] = [
    {
      id: `company-${contact_company.id}`,
      label: companyLabel,
      icon: PiBuildings,
      defaultExpanded: true,
      children: sizeNode,
    },
  ]

  return <TreeList nodes={nodes} />
}

// ─── Related Companies Section ───────────────────────────────────────────────

/** Sidebar tree showing secondary related companies with opp type. */
function RelatedCompaniesSection({ deal }: { deal: DealDetail }) {
  const relatedCompanies = deal.related_companies ?? []

  if (relatedCompanies.length === 0) {
    return (
      <p className="text-sm text-fg-secondary py-1">
        No related companies yet.
      </p>
    )
  }

  const nodes: TreeNode[] = relatedCompanies.map((rc) => ({
    id: `related-${rc.id}`,
    label: rc.url ? (
      <Link to={rc.url} className="text-brand-bright font-medium">
        {rc.name}
      </Link>
    ) : (
      <span className="text-fg font-medium">{rc.name}</span>
    ),
    icon: PiBuildings,
    children: rc.sub_category
      ? [
          {
            id: `related-${rc.id}-type`,
            label: (
              <span className="text-sm text-fg-secondary">
                {rc.sub_category.name}
              </span>
            ),
          },
        ]
      : [],
  }))

  return <TreeList nodes={nodes} />
}

// ─── Tab Bar ──────────────────────────────────────────────────────────────────

const DEAL_DETAIL_TABS: ResponsiveTabConfig<DealDetailTab>[] = [
  { key: 'activity', title: 'All Activity', icon: PiListBullets },
  { key: 'emails', title: 'Emails', icon: PiEnvelopeOpen },
  { key: 'notes', title: 'Notes', icon: PiNotepad },
  { key: 'reminders', title: 'Reminders', icon: PiCalendarStar },
  { key: 'documents', title: 'Documents', icon: PiFileText },
  { key: 'recommendations', title: 'Recommendations', icon: PiSparkle },
]

// ─── Note Composer ────────────────────────────────────────────────────────────

const dealNoteSchema = z.object({
  note: z.string().optional().nullable(),
  attachments: z.any().optional().nullable(),
})

type DealNoteInput = z.infer<typeof dealNoteSchema>

/** Rich-text note composer with attachment support for the deal detail page. */
function DealNoteComposer({
  dealId,
  onNoteSaved,
}: {
  dealId: number
  onNoteSaved: () => void
}) {
  const api = useApiService()
  const [isSaving, setIsSaving] = useState(false)
  const { control, handleSubmit, reset } = useForm<DealNoteInput>({
    defaultValues: { note: '', attachments: [] },
    resolver: zodResolver(dealNoteSchema),
  })
  const noteText = useWatch({ control, name: 'note' })

  const onSubmit = handleSubmit(async (values) => {
    if (!values.note?.trim()) return
    setIsSaving(true)
    try {
      await api.createDealNote(
        dealId,
        values.note || '',
        values.attachments || []
      )
      toast.success('Note saved')
      reset({ note: '', attachments: [] })
      onNoteSaved()
    } catch {
      toast.error('Failed to save note')
    } finally {
      setIsSaving(false)
    }
  })

  return (
    <div
      className={cn(
        'bg-white rounded-md shadow-sm border border-line overflow-hidden',
        // Strip the inner editor's own top margin and the Quill
        // container's own border + rounded corners so this wrapper
        // owns the entire visual shell with no double-stroke at any
        // seam. Mirrors the CompanyTopCard quick-note composer.
        '[&_.mt-2]:!mt-0',
        '[&_.ql-container]:!border-0 [&_.ql-container]:!rounded-none',
        '[&_.ql-toolbar]:!border-0 [&_.ql-toolbar]:!rounded-none'
      )}
    >
      <NoteManager
        control={control}
        noteName="note"
        attachmentsName="attachments"
        placeholder="Start writing your note here"
        enableMentions
        editorClassName="h-36 bg-white border-0"
        toolbarClassName="bg-white border-0"
        label=""
        disabled={isSaving}
        toolbarSuffix={
          // Always render the buttons so the toolbar height stays
          // constant; hide them visually (and disable pointer events)
          // until there's actual note text.
          <span
            className={cn(
              'inline-flex items-center gap-2',
              !noteText?.trim() && 'invisible pointer-events-none'
            )}
          >
            <button
              type="button"
              onClick={() => reset({ note: '', attachments: [] })}
              className="text-sm text-fg-secondary hover:text-fg transition-colors px-2 py-1"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSubmit}
              disabled={!noteText?.trim() || isSaving}
              className={cn(
                'text-sm font-medium transition-colors px-2 py-1',
                noteText?.trim()
                  ? 'text-brand hover:text-brand-hover'
                  : 'text-fg-secondary cursor-not-allowed'
              )}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </span>
        }
      />
    </div>
  )
}

// ─── Notes Tab ────────────────────────────────────────────────────────────────

const DEAL_NOTES_FILTER_ID: FilterIdentifier = 'DEAL_NOTES_TABLE'

/**
 * Dedicated notes list with filters that fetches from the deal notes API
 * and renders in the activity timeline visual structure (pistachio date groups).
 */
function DealNotesTab({
  dealId,
  onNotesChanged,
  stickyTopOffset = 0,
}: {
  dealId: number
  onNotesChanged: () => void
  stickyTopOffset?: number
}) {
  const api = useApiService()
  const [notes, setNotes] = useState<INote[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null)
  const [dateRange, setDateRange] = useState<
    { from: Date; to?: Date } | undefined
  >(undefined)

  const dealNotesFilterStore = filterStoreRepo.getStore(DEAL_NOTES_FILTER_ID)
  const filterParams = useFilterParams(dealNotesFilterStore)

  /** Merge filter modal params with the visible date range picker params. */
  const allParams = useMemo(() => {
    const params: Record<string, any> = { ...filterParams }
    if (dateRange?.from) {
      params.created_greater = format(dateRange.from, 'yyyy-MM-dd')
    }
    if (dateRange?.to) {
      params.created_less = format(dateRange.to, 'yyyy-MM-dd')
    }
    return params
  }, [filterParams, dateRange])
  const allParamsKey = JSON.stringify(allParams)

  const hasActiveFilters = useMemo(() => {
    return Object.keys(filterParams).length > 0 || !!dateRange
  }, [filterParams, dateRange])

  const hasNextPage = notes.length < totalCount

  /** Fetch a specific page of notes. Resets to page 1 when called without args. */
  const fetchNotes = useCallback(
    async (pageNum = 1, append = false) => {
      if (append) {
        setIsLoadingMore(true)
      } else {
        setIsLoading(true)
      }
      try {
        const data = await api.getDealNotes(dealId, {
          ...allParams,
          page: pageNum,
        })
        if (append) {
          setNotes((prev) => [...prev, ...data.results])
        } else {
          setNotes(data.results)
        }
        setTotalCount(data.count)
        setPage(pageNum)
      } catch {
        toast.error('Failed to load notes')
      } finally {
        setIsLoading(false)
        setIsLoadingMore(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dealId, allParamsKey]
  )

  // Reset to page 1 when filters change
  useEffect(() => {
    fetchNotes(1)
  }, [fetchNotes])

  const handleLoadMore = useCallback(() => {
    fetchNotes(page + 1, true)
  }, [fetchNotes, page])

  const handleDelete = useCallback(
    async (noteId: number) => {
      try {
        await api.deleteDealNote(dealId, noteId)
        setNotes((prev) => prev.filter((n) => n.id !== noteId))
        setTotalCount((c) => c - 1)
        toast.success('Note deleted')
        onNotesChanged()
      } catch {
        toast.error('Failed to delete note')
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dealId, onNotesChanged]
  )

  const handleEditSave = useCallback(
    async (
      noteId: number,
      content: string,
      attachmentsToDelete: number[],
      newAttachments: File[]
    ) => {
      try {
        await api.editDealNote(
          dealId,
          noteId,
          content,
          attachmentsToDelete,
          newAttachments
        )
        setEditingNoteId(null)
        fetchNotes()
        toast.success('Note updated')
      } catch {
        toast.error('Failed to update note')
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dealId, fetchNotes]
  )

  const groupedNotes = useMemo(
    () => groupByDate(notes, (n) => n.created),
    [notes]
  )

  return (
    <div>
      {/* Filters — sticky below main header */}
      <div
        className="sticky z-[19] bg-surface pb-2 flex flex-col gap-2"
        style={{ top: stickyTopOffset }}
      >
        <div className="flex items-center gap-2">
          <DateRangePicker value={dateRange} onChange={setDateRange} />
          <DealNotesFilterset filterIdentifier={DEAL_NOTES_FILTER_ID} />
        </div>

        <TableLinkStyleProvider variant="plain">
          <FilterChips
            store={dealNotesFilterStore}
            identifier={DEAL_NOTES_FILTER_ID}
            showActive
            clearAllButton
          />
        </TableLinkStyleProvider>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center h-48">
          <Spinner animation="border" size="sm" />
        </div>
      ) : notes.length === 0 ? (
        <div className="py-8 text-center text-base text-gray-500">
          {hasActiveFilters ? 'No notes match your filters' : 'No notes yet'}
        </div>
      ) : (
        <div className="space-y-4">
          {groupedNotes.map((group) => (
            <TimelineDateGroup key={group.date} displayDate={group.displayDate}>
              {group.items.map((note) => (
                <DealNoteCard
                  key={note.id}
                  note={note}
                  isEditing={editingNoteId === note.id}
                  onEdit={() => setEditingNoteId(note.id)}
                  onCancelEdit={() => setEditingNoteId(null)}
                  onSaveEdit={(content, attachmentsToDelete, newAttachments) =>
                    handleEditSave(
                      note.id,
                      content,
                      attachmentsToDelete,
                      newAttachments
                    )
                  }
                  onDelete={() => handleDelete(note.id)}
                />
              ))}
            </TimelineDateGroup>
          ))}
          {hasNextPage && (
            <LoadMoreButton
              onClick={handleLoadMore}
              isLoading={isLoadingMore}
              label="Load More Notes"
            />
          )}
        </div>
      )}
    </div>
  )
}

// ─── Note Card ───────────────────────────────────────────────────────────────

/** Two-click delete button — first click shows confirm icon, second click executes. */
function TwoClickDeleteButton({
  onDelete,
  title = 'Delete',
}: {
  onDelete: () => void
  title?: string
}) {
  const [confirmDelete, setConfirmDelete] = useState(false)

  if (confirmDelete) {
    return (
      <button
        onClick={() => {
          onDelete()
          setConfirmDelete(false)
        }}
        className="p-1 text-error hover:text-error transition-colors rounded"
        title={`Confirm ${title.toLowerCase()}`}
      >
        <PiCheck size={14} />
      </button>
    )
  }

  return (
    <button
      onClick={() => setConfirmDelete(true)}
      onBlur={() => setTimeout(() => setConfirmDelete(false), 200)}
      className="p-1 text-fg-secondary hover:text-error transition-colors rounded"
      title={title}
    >
      <PiTrash size={14} />
    </button>
  )
}

/** Paginated "Load More" button matching the activity page pattern. */
function LoadMoreButton({
  onClick,
  isLoading,
  label,
}: {
  onClick: () => void
  isLoading: boolean
  label: string
}) {
  return (
    <div className="flex justify-center pt-4 pb-8">
      <button
        type="button"
        onClick={onClick}
        disabled={isLoading}
        className="inline-flex items-center justify-center gap-2 h-9 px-4 rounded-lg bg-brand-bright text-white text-base font-medium hover:bg-brand-active transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? (
          <>
            <Spinner animation="border" size="sm" />
            <span>Loading...</span>
          </>
        ) : (
          <span>{label}</span>
        )}
      </button>
    </div>
  )
}

/** Individual note card with inline edit (using NoteManager), two-click delete, and attachment links. */
function DealNoteCard({
  note,
  isEditing,
  onEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
}: {
  note: INote
  isEditing: boolean
  onEdit: () => void
  onCancelEdit: () => void
  onSaveEdit: (
    content: string,
    attachmentsToDelete: number[],
    newAttachments: File[]
  ) => void
  onDelete: () => void
}) {
  const [isSaving, setIsSaving] = useState(false)
  const [attachmentIdsToDelete, setAttachmentIdsToDelete] = useState<number[]>(
    []
  )
  const userName =
    `${note.user.first_name} ${note.user.last_name}`.trim() || 'Unknown'
  const time = dayjs(note.created).format('h:mm A')

  const { control, handleSubmit } = useForm<DealNoteInput>({
    defaultValues: { note: note.content, attachments: [] },
    resolver: zodResolver(dealNoteSchema),
  })

  const handleSave = handleSubmit(async (values) => {
    setIsSaving(true)
    try {
      await onSaveEdit(
        values.note || '',
        attachmentIdsToDelete,
        values.attachments || []
      )
    } finally {
      setIsSaving(false)
    }
  })

  return (
    <DealActivityCardShell
      icon={<PiNotepad className="w-5 h-5 text-green-700" />}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-inter text-base leading-[1.43] text-gray-500">
            <span className="text-gray-700 font-medium">{userName}</span> added
            a <span className="text-gray-700 font-medium">note</span>
          </div>

          {isEditing ? (
            <NoteManager
              control={control}
              noteName="note"
              attachmentsName="attachments"
              placeholder="Edit your note..."
              enableMentions
              editorClassName="h-28 bg-surface-alt"
              toolbarClassName="bg-surface-alt"
              label=""
              disabled={isSaving}
              existingNote={note}
              onAttachmentsToDelete={setAttachmentIdsToDelete}
              toolbarSuffix={
                <>
                  <button
                    type="button"
                    onClick={onCancelEdit}
                    className="text-sm text-fg-secondary hover:text-fg transition-colors px-2 py-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={isSaving}
                    className="text-sm font-medium transition-colors px-2 py-1 text-brand hover:text-brand-hover"
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                </>
              }
            />
          ) : (
            <div className="text-base text-gray-700">
              <Editor value={note.content} readOnly disableToolbar borderless />
            </div>
          )}
        </div>

        <div className="flex-shrink-0 flex items-center gap-1.5">
          <span className="font-inter text-base leading-[1.43] text-right text-gray-500 tabular-nums">
            {time}
          </span>
          {!isEditing && (
            <>
              <button
                type="button"
                onClick={onEdit}
                className="p-1 text-fg-secondary hover:text-brand transition-colors rounded"
                title="Edit note"
              >
                <PiPencilSimple size={14} />
              </button>
              <TwoClickDeleteButton onDelete={onDelete} title="Delete note" />
            </>
          )}
        </div>
      </div>

      {!isEditing && note.attachments.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2 justify-end">
          {note.attachments.map((att: any) => (
            <a
              key={att.id}
              href={att.file}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-brand hover:underline"
            >
              <PiPaperclip size={12} />
              {att.original_filename}
            </a>
          ))}
        </div>
      )}
    </DealActivityCardShell>
  )
}

// ─── Reminders Tab ───────────────────────────────────────────────────────────

const DEAL_REMINDERS_FILTER_ID: FilterIdentifier = 'DEAL_REMINDERS_TABLE'

/** Full reminders tab with filters, collapsible cards, create/edit/delete. */
function DealRemindersTab({
  dealId,
  contactCompanyId,
  onRemindersChanged,
  stickyTopOffset = 0,
}: {
  dealId: number
  contactCompanyId?: number
  onRemindersChanged: () => void
  stickyTopOffset?: number
}) {
  const api = useApiService()
  const [reminders, setReminders] = useState<ReminderListItem[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [showInactive, setShowInactive] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingReminderId, setEditingReminderId] = useState<number | null>(
    null
  )
  const [dateRange, setDateRange] = useState<
    { from: Date; to?: Date } | undefined
  >(undefined)

  const remindersFilterStore = filterStoreRepo.getStore(
    DEAL_REMINDERS_FILTER_ID
  )
  const filterParams = useFilterParams(remindersFilterStore)

  const allParams = useMemo(() => {
    const params: Record<string, any> = {
      ...filterParams,
      deal: dealId,
    }
    if (dateRange?.from) {
      params.scheduled_after = format(dateRange.from, 'yyyy-MM-dd')
    }
    if (dateRange?.to) {
      params.scheduled_before = format(dateRange.to, 'yyyy-MM-dd')
    }
    if (!showInactive) {
      params.is_active = 'true'
    }
    return params
  }, [filterParams, dateRange, dealId, showInactive])
  const allParamsKey = JSON.stringify(allParams)

  const hasActiveFilters = useMemo(() => {
    return Object.keys(filterParams).length > 0 || !!dateRange || showInactive
  }, [filterParams, dateRange, showInactive])

  const hasNextPage = reminders.length < totalCount

  /** Fetch a specific page of reminders. Resets to page 1 when called without args. */
  const fetchReminders = useCallback(
    async (pageNum = 1, append = false) => {
      if (append) {
        setIsLoadingMore(true)
      } else {
        setIsLoading(true)
      }
      try {
        const data = await api.getReminders({ ...allParams, page: pageNum })
        if (append) {
          setReminders((prev) => [...prev, ...(data.results ?? [])])
        } else {
          setReminders(data.results ?? [])
        }
        setTotalCount(data.count)
        setPage(pageNum)
      } catch {
        toast.error('Failed to load reminders')
      } finally {
        setIsLoading(false)
        setIsLoadingMore(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dealId, allParamsKey]
  )

  // Reset to page 1 when filters change
  useEffect(() => {
    fetchReminders(1)
  }, [fetchReminders])

  const handleLoadMore = useCallback(() => {
    fetchReminders(page + 1, true)
  }, [fetchReminders, page])

  const handleDelete = useCallback(
    async (reminderId: number) => {
      try {
        await api.deleteReminder(reminderId)
        setReminders((prev) => prev.filter((r) => r.id !== reminderId))
        setTotalCount((c) => c - 1)
        toast.success('Reminder deleted')
        onRemindersChanged()
      } catch {
        toast.error('Failed to delete reminder')
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onRemindersChanged]
  )

  const handlePauseToggle = useCallback(
    async (reminder: ReminderListItem) => {
      try {
        if (reminder.status === 'paused') {
          await api.unpauseReminder(reminder.id)
          toast.success('Reminder resumed')
        } else {
          await api.pauseReminder(reminder.id)
          toast.success('Reminder paused')
        }
        fetchReminders()
      } catch {
        toast.error('Failed to update reminder')
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchReminders]
  )

  const groupedReminders = useMemo(() => {
    const fireDate = (r: ReminderListItem) =>
      r.next_run_at || r.run_at || r.created
    const sorted = [...reminders].sort(
      (a, b) => dayjs(fireDate(a)).valueOf() - dayjs(fireDate(b)).valueOf()
    )
    return groupByDate(sorted, fireDate).sort((a, b) =>
      a.date.localeCompare(b.date)
    )
  }, [reminders])

  return (
    <div>
      {/* Filters — sticky below main header */}
      <div
        className="sticky z-[19] bg-surface pb-2 flex flex-col gap-2"
        style={{ top: stickyTopOffset }}
      >
        <div className="flex items-center gap-2">
          {/* Date Range Picker */}
          <DateRangePicker value={dateRange} onChange={setDateRange} />

          {/* Show Inactive Toggle */}
          <button
            onClick={() => setShowInactive((v) => !v)}
            className={cn(
              'px-3 min-h-[38px] text-sm rounded-md border transition-colors',
              showInactive
                ? 'bg-brand text-fg-inverse border-brand'
                : 'bg-surface text-fg-secondary border-line hover:border-brand'
            )}
          >
            Show inactive
          </button>

          {/* More Filters */}
          <DealRemindersFilterset filterIdentifier={DEAL_REMINDERS_FILTER_ID} />
        </div>

        <TableLinkStyleProvider variant="plain">
          <FilterChips
            store={remindersFilterStore}
            identifier={DEAL_REMINDERS_FILTER_ID}
            showActive
            clearAllButton
          />
        </TableLinkStyleProvider>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center h-48">
          <Spinner animation="border" size="sm" />
        </div>
      ) : reminders.length === 0 ? (
        <div className="py-8 text-center text-base text-gray-500">
          {hasActiveFilters ? (
            'No reminders match your filters'
          ) : (
            <div className="flex flex-col items-center gap-3">
              <p className="m-0">No reminders yet</p>
              <FbButton
                variant="secondary"
                size="sm"
                onClick={() => setShowCreateModal(true)}
              >
                <PiBell size={14} />
                Schedule your first reminder
              </FbButton>
            </div>
          )}
        </div>
      ) : (
        <>
          {groupedReminders.map((group) => (
            <TimelineDateGroup key={group.date} displayDate={group.displayDate}>
              {group.items.map((reminder) => (
                <DealReminderCard
                  key={reminder.id}
                  reminder={reminder}
                  onEdit={() => setEditingReminderId(reminder.id)}
                  onDelete={() => handleDelete(reminder.id)}
                  onPauseToggle={() => handlePauseToggle(reminder)}
                />
              ))}
            </TimelineDateGroup>
          ))}
          {hasNextPage && (
            <LoadMoreButton
              onClick={handleLoadMore}
              isLoading={isLoadingMore}
              label="Load More Reminders"
            />
          )}
        </>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <ReminderModal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          defaultDealIds={[dealId]}
          defaultCompanyId={contactCompanyId}
          onCreated={() => {
            fetchReminders()
            onRemindersChanged()
          }}
        />
      )}

      {/* Edit Modal */}
      {editingReminderId && (
        <ReminderModal
          open={!!editingReminderId}
          onClose={() => setEditingReminderId(null)}
          reminderId={editingReminderId}
          onUpdated={() => {
            fetchReminders()
            onRemindersChanged()
          }}
        />
      )}
    </div>
  )
}

// ─── Reminder Card ───────────────────────────────────────────────────────────

/** Flat reminder card matching the note card layout — icon + content with inline actions. */
function DealReminderCard({
  reminder,
  onEdit,
  onDelete,
  onPauseToggle,
}: {
  reminder: ReminderListItem
  onEdit: () => void
  onDelete: () => void
  onPauseToggle: () => void
}) {
  const canPause =
    reminder.reminder_type !== 'one_off' &&
    (reminder.status === 'active' || reminder.status === 'paused')
  const createdByName = reminder.created_by_name || 'Someone'

  return (
    <DealActivityCardShell
      icon={<PiAlarm className="w-5 h-5 text-green-700" />}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-inter text-base leading-[1.43] text-gray-500">
            <span className="text-gray-700 font-medium">{createdByName}</span>{' '}
            scheduled a{' '}
            <span className="text-gray-700 font-medium">reminder</span>
            {reminder.title && (
              <>
                : <span className="font-medium text-fg">{reminder.title}</span>
              </>
            )}
          </div>

          <div className="mt-1 flex items-center gap-1.5 flex-wrap text-xxs">
            <span
              className={cn(
                'inline-flex items-center px-1.5 h-5 rounded font-medium',
                statusColors[reminder.status]
              )}
            >
              {reminder.status.charAt(0).toUpperCase() +
                reminder.status.slice(1)}
            </span>
            <span className="inline-flex items-center px-1.5 h-5 rounded font-medium bg-surface-hover text-fg-secondary">
              {reminderTypeFormat(reminder)}
            </span>
            {(reminder.next_run_at || reminder.run_at) && (
              <span className="text-fg-secondary">
                {dayjs(reminder.next_run_at || reminder.run_at).format(
                  'MMM D, YYYY [at] h:mm A'
                )}
              </span>
            )}
            {reminder.recipients_names &&
              reminder.recipients_names.length > 0 && (
                <span className="flex items-center gap-1 text-fg-secondary">
                  <PiUser size={10} />
                  {reminder.recipients_names.slice(0, 3).join(', ')}
                  {reminder.recipients_names.length > 3 &&
                    ` +${reminder.recipients_names.length - 3}`}
                </span>
              )}
          </div>

          {reminder.content && (
            <div className="mt-1 text-base text-gray-700">
              <Editor
                value={reminder.content}
                readOnly
                disableToolbar
                borderless
              />
            </div>
          )}
        </div>

        <div className="flex-shrink-0 flex items-center gap-1.5">
          <span className="font-inter text-base leading-[1.43] text-right text-gray-500 tabular-nums">
            {dayjs(reminder.created).format('h:mm A')}
          </span>
          <button
            type="button"
            onClick={onEdit}
            className="p-1 text-fg-secondary hover:text-brand transition-colors rounded"
            title="Edit reminder"
          >
            <PiPencilSimple size={14} />
          </button>
          {canPause && (
            <button
              type="button"
              onClick={onPauseToggle}
              className="p-1 text-fg-secondary hover:text-brand transition-colors rounded"
              title={reminder.status === 'paused' ? 'Resume' : 'Pause'}
            >
              {reminder.status === 'paused' ? (
                <PiBell size={14} />
              ) : (
                <PiBell size={14} className="opacity-50" />
              )}
            </button>
          )}
          <TwoClickDeleteButton onDelete={onDelete} title="Delete reminder" />
        </div>
      </div>
    </DealActivityCardShell>
  )
}

// ─── Documents Tab ───────────────────────────────────────────────────────────

const DEAL_DOCUMENTS_FILTER_ID: FilterIdentifier = 'DEAL_DOCUMENTS_TABLE'
const DOCUMENTS_PAGE_SIZE = 20

/** Timeline-style documents tab matching notes/reminders visual pattern. */
function DealDocumentsTab({
  dealId,
  onDocumentsChanged,
  stickyTopOffset = 0,
  refreshKey = 0,
}: {
  dealId: number
  onDocumentsChanged: () => void
  stickyTopOffset?: number
  /** Increment to trigger a refetch (e.g. after external upload). */
  refreshKey?: number
}) {
  const [documents, setDocuments] = useState<Document[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [dateRange, setDateRange] = useState<
    { from: Date; to?: Date } | undefined
  >(undefined)
  const deleteDocumentMutation = useDeleteDocument()

  const docsFilterStore = filterStoreRepo.getStore(DEAL_DOCUMENTS_FILTER_ID)
  const filterParams = useFilterParams(docsFilterStore)

  /** Merge filter modal params with the visible date range picker params. */
  const allParams = useMemo(() => {
    const params: Record<string, any> = {
      ...filterParams,
      deal: dealId,
      ordering: '-created',
    }
    if (dateRange?.from) {
      params.created_greater = format(dateRange.from, 'yyyy-MM-dd')
    }
    if (dateRange?.to) {
      params.created_less = format(dateRange.to, 'yyyy-MM-dd')
    }
    return params
  }, [filterParams, dateRange, dealId])
  const allParamsKey = JSON.stringify(allParams)

  const hasActiveFilters = useMemo(() => {
    return Object.keys(filterParams).length > 0 || !!dateRange
  }, [filterParams, dateRange])

  const hasNextPage = documents.length < totalCount

  /** Fetch a specific page of documents. Resets to page 1 when called without args. */
  const fetchDocuments = useCallback(
    async (pageNum = 1, append = false) => {
      if (append) {
        setIsLoadingMore(true)
      } else {
        setIsLoading(true)
      }
      try {
        const data = await getDocuments({
          ...allParams,
          page: pageNum,
          page_size: DOCUMENTS_PAGE_SIZE,
        })
        if (append) {
          setDocuments((prev) => [...prev, ...data.results])
        } else {
          setDocuments(data.results)
        }
        setTotalCount(data.count)
        setPage(pageNum)
      } catch {
        toast.error('Failed to load documents')
      } finally {
        setIsLoading(false)
        setIsLoadingMore(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dealId, allParamsKey, refreshKey]
  )

  useEffect(() => {
    fetchDocuments(1)
  }, [fetchDocuments])

  const handleLoadMore = useCallback(() => {
    fetchDocuments(page + 1, true)
  }, [fetchDocuments, page])

  const handleDelete = useCallback(
    async (docId: number) => {
      try {
        await deleteDocumentMutation.mutateAsync({ id: docId })
        setDocuments((prev) => prev.filter((d) => d.id !== docId))
        setTotalCount((c) => c - 1)
        toast.success('Document deleted')
        onDocumentsChanged()
      } catch {
        toast.error('Failed to delete document')
      }
    },
    [onDocumentsChanged, deleteDocumentMutation]
  )

  const handleDownload = useCallback(async (doc: Document) => {
    if (!doc.file) {
      toast.error('No file available for this document')
      return
    }
    try {
      const response = await fetch(doc.file)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = doc.name
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch {
      toast.error('Failed to download file')
    }
  }, [])

  const groupedDocuments = useMemo(
    () => groupByDate(documents, (d) => d.created),
    [documents]
  )

  return (
    <div>
      {/* Filters — sticky below main header */}
      <div
        className="sticky z-[19] bg-surface pb-2 flex flex-col gap-2"
        style={{ top: stickyTopOffset }}
      >
        <div className="flex items-center gap-2">
          <DateRangePicker value={dateRange} onChange={setDateRange} />
          <DealDocumentsFilterset filterIdentifier={DEAL_DOCUMENTS_FILTER_ID} />
        </div>

        <TableLinkStyleProvider variant="plain">
          <FilterChips
            store={docsFilterStore}
            identifier={DEAL_DOCUMENTS_FILTER_ID}
            showActive
            clearAllButton
          />
        </TableLinkStyleProvider>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center h-48">
          <Spinner animation="border" size="sm" />
        </div>
      ) : documents.length === 0 ? (
        <div className="py-8 text-center text-base text-gray-500">
          {hasActiveFilters
            ? 'No documents match your filters'
            : 'No documents yet'}
        </div>
      ) : (
        <div className="space-y-4">
          {groupedDocuments.map((group) => (
            <TimelineDateGroup key={group.date} displayDate={group.displayDate}>
              {group.items.map((doc) => (
                <DealDocumentCard
                  key={doc.id}
                  document={doc}
                  onDownload={() => handleDownload(doc)}
                  onDelete={() => handleDelete(doc.id)}
                />
              ))}
            </TimelineDateGroup>
          ))}
          {hasNextPage && (
            <LoadMoreButton
              onClick={handleLoadMore}
              isLoading={isLoadingMore}
              label="Load More Documents"
            />
          )}
        </div>
      )}
    </div>
  )
}

// ─── Document Card ──────────────────────────────────────────────────────────

/** Individual document card matching the note/reminder card layout. */
function DealDocumentCard({
  document: doc,
  onDownload,
  onDelete,
}: {
  document: Document
  onDownload: () => void
  onDelete: () => void
}) {
  const uploaderName = doc.created_by_name || 'Someone'
  const time = dayjs(doc.created).format('h:mm A')

  return (
    <DealActivityCardShell
      icon={<PiFileText className="w-5 h-5 text-green-700" />}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-inter text-base leading-[1.43] text-gray-500">
            <span className="text-gray-700 font-medium">{uploaderName}</span>{' '}
            uploaded a{' '}
            <span className="text-gray-700 font-medium">document</span>
            {': '}
            <span className="font-medium text-fg">{doc.name}</span>
          </div>

          <div className="mt-1 flex items-center gap-1.5 flex-wrap text-xxs">
            {doc.file_extension && (
              <span className="inline-flex items-center px-1.5 h-5 rounded font-medium bg-surface-hover text-fg-secondary uppercase">
                {doc.file_extension}
              </span>
            )}
            <span className="inline-flex items-center px-1.5 h-5 rounded font-medium bg-surface-hover text-fg-secondary">
              {formatBytes(doc.size ?? 0)}
            </span>
            {doc.tags.length > 0 && (
              <span className="text-fg-secondary">
                {doc.tags.map((t) => t.tag).join(', ')}
              </span>
            )}
          </div>
        </div>

        <div className="flex-shrink-0 flex items-center gap-1.5">
          <span className="font-inter text-base leading-[1.43] text-right text-gray-500 tabular-nums">
            {time}
          </span>
          <button
            type="button"
            onClick={onDownload}
            className="p-1 text-fg-secondary hover:text-brand transition-colors rounded"
            title="Download"
          >
            <PiDownloadSimple size={14} />
          </button>
          <TwoClickDeleteButton onDelete={onDelete} title="Delete document" />
        </div>
      </div>
    </DealActivityCardShell>
  )
}
